import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Grid,
  Alert,
  CircularProgress
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { leadsService } from '../../services/leads';
import { callsService, CALL_PURPOSES, CALL_PURPOSE_LABELS } from '../../services/calls';
import { formatDateTimeForAPI } from '../../utils/helpers';

const FollowUpDialog = ({ 
  open, 
  onClose, 
  lead, 
  onSuccess,
  mode = 'update' // 'update', 'schedule', 'log'
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const [followUpData, setFollowUpData] = useState({
    status: 'followup',
    follow_up_date: lead?.follow_up_date || '',
    notes: '',
    last_contacted: new Date().toISOString()
  });

  const [callScheduleData, setCallScheduleData] = useState({
    scheduled_time: new Date().toISOString(),
    purpose: 'follow_up',
    notes: ''
  });

  const [callLogData, setCallLogData] = useState({
    call_type: 'outgoing',
    duration: '00:05:00',
    status: 'completed',
    notes: '',
    started_at: new Date().toISOString(),
    ended_at: new Date().toISOString()
  });

  const handleUpdateFollowUp = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Validate that the lead has all required fields
      if (!lead.name || !lead.phone || !lead.city || !lead.disease) {
        setError('Lead is missing required fields. Please edit the lead first to add name, phone, city, and disease.');
        setLoading(false);
        return;
      }
      
      // Include required lead fields along with follow-up data
      const updateData = {
        ...followUpData,
        // Required fields that must be included
        name: lead.name,
        phone: lead.phone,
        city: lead.city,
        disease: lead.disease,
        // Include other existing lead data to avoid overwriting
        email: lead.email,
        age: lead.age,
        address: lead.address,
        symptoms: lead.symptoms,
        medical_history: lead.medical_history,
        priority: lead.priority,
        lead_source: lead.lead_source
      };
      
      await leadsService.updateLeadFollowUp(lead.id, updateData);
      
      // Add comment if notes provided
      if (followUpData.notes) {
        await leadsService.addFollowUpComment(lead.id, {
          comment: followUpData.notes,
          is_internal: false
        });
      }
      
      onSuccess?.();
      onClose();
    } catch (err) {
      console.error('Follow-up update error:', err);
      
      // Handle validation errors specifically
      if (err.response?.data) {
        const errorData = err.response.data;
        let errorMessage = 'Failed to update follow-up';
        
        if (typeof errorData === 'object') {
          const requiredFields = ['name', 'phone', 'city', 'disease'];
          const missingFields = requiredFields.filter(field => errorData[field]);
          
          if (missingFields.length > 0) {
            errorMessage = `Missing required fields: ${missingFields.join(', ')}`;
          } else {
            // Check for other validation errors
            const errorFields = Object.keys(errorData);
            if (errorFields.length > 0) {
              errorMessage = `Validation errors: ${errorFields.join(', ')}`;
            }
          }
        } else if (typeof errorData === 'string') {
          errorMessage = errorData;
        }
        
        setError(errorMessage);
      } else {
        setError(err.message || 'Failed to update follow-up');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleScheduleCall = async () => {
    try {
      setLoading(true);
      setError('');
      
      await callsService.scheduleFollowUpCall({
        ...callScheduleData,
        lead: lead.id,
        assigned_to: lead.assigned_to || null
      });
      
      onSuccess?.();
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to schedule call');
    } finally {
      setLoading(false);
    }
  };

  const handleLogCall = async () => {
    try {
      setLoading(true);
      setError('');
      
      await callsService.createCallLog({
        ...callLogData,
        lead: lead.id,
        phone_number: lead.phone
      });
      
      onSuccess?.();
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to log call');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = () => {
    switch (mode) {
      case 'update':
        return handleUpdateFollowUp();
      case 'schedule':
        return handleScheduleCall();
      case 'log':
        return handleLogCall();
      default:
        return handleUpdateFollowUp();
    }
  };

  const getDialogTitle = () => {
    switch (mode) {
      case 'update':
        return 'Update Follow-up';
      case 'schedule':
        return 'Schedule Follow-up Call';
      case 'log':
        return 'Log Follow-up Call';
      default:
        return 'Follow-up';
    }
  };

  const renderUpdateContent = () => (
    <Box sx={{ pt: 2 }}>
      <DateTimePicker
        label="Follow-up Date & Time"
        value={followUpData.follow_up_date ? new Date(followUpData.follow_up_date) : null}
        onChange={(date) => setFollowUpData({
          ...followUpData,
          follow_up_date: formatDateTimeForAPI(date)
        })}
        slotProps={{
          textField: {
            fullWidth: true,
            margin: 'normal'
          }
        }}
      />
      
      <TextField
        fullWidth
        multiline
        rows={4}
        label="Follow-up Notes"
        value={followUpData.notes}
        onChange={(e) => setFollowUpData({
          ...followUpData,
          notes: e.target.value
        })}
        margin="normal"
        placeholder="Patient needs follow-up consultation after surgery..."
      />
    </Box>
  );

  const renderScheduleContent = () => (
    <Box sx={{ pt: 2 }}>
      <DateTimePicker
        label="Call Date & Time"
        value={callScheduleData.scheduled_time ? new Date(callScheduleData.scheduled_time) : null}
        onChange={(date) => setCallScheduleData({
          ...callScheduleData,
          scheduled_time: date?.toISOString()
        })}
        slotProps={{
          textField: {
            fullWidth: true,
            margin: 'normal'
          }
        }}
      />
      
      <FormControl fullWidth margin="normal">
        <InputLabel>Call Purpose</InputLabel>
        <Select
          value={callScheduleData.purpose}
          onChange={(e) => setCallScheduleData({
            ...callScheduleData,
            purpose: e.target.value
          })}
          label="Call Purpose"
        >
          {Object.entries(CALL_PURPOSE_LABELS).map(([value, label]) => (
            <MenuItem key={value} value={value}>{label}</MenuItem>
          ))}
        </Select>
      </FormControl>
      
      <TextField
        fullWidth
        multiline
        rows={3}
        label="Call Notes"
        value={callScheduleData.notes}
        onChange={(e) => setCallScheduleData({
          ...callScheduleData,
          notes: e.target.value
        })}
        margin="normal"
        placeholder="Follow up on treatment progress, check recovery status..."
      />
    </Box>
  );

  const renderLogContent = () => (
    <Box sx={{ pt: 2 }}>
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <DateTimePicker
            label="Call Started"
            value={callLogData.started_at ? new Date(callLogData.started_at) : null}
            onChange={(date) => setCallLogData({
              ...callLogData,
              started_at: date?.toISOString()
            })}
            slotProps={{
              textField: {
                fullWidth: true,
                size: 'small'
              }
            }}
          />
        </Grid>
        <Grid item xs={6}>
          <DateTimePicker
            label="Call Ended"
            value={callLogData.ended_at ? new Date(callLogData.ended_at) : null}
            onChange={(date) => setCallLogData({
              ...callLogData,
              ended_at: date?.toISOString()
            })}
            slotProps={{
              textField: {
                fullWidth: true,
                size: 'small'
              }
            }}
          />
        </Grid>
      </Grid>
      
      <TextField
        fullWidth
        label="Call Duration (MM:SS)"
        value={callLogData.duration}
        onChange={(e) => setCallLogData({
          ...callLogData,
          duration: e.target.value
        })}
        margin="normal"
        placeholder="05:30"
        helperText="Format: MM:SS (e.g., 05:30 for 5 minutes 30 seconds)"
      />

      <FormControl fullWidth margin="normal">
        <InputLabel>Call Status</InputLabel>
        <Select
          value={callLogData.status}
          onChange={(e) => setCallLogData({
            ...callLogData,
            status: e.target.value
          })}
          label="Call Status"
        >
          <MenuItem value="completed">Completed</MenuItem>
          <MenuItem value="missed">Missed</MenuItem>
          <MenuItem value="cancelled">Cancelled</MenuItem>
        </Select>
      </FormControl>
      
      <TextField
        fullWidth
        multiline
        rows={4}
        label="Call Summary"
        value={callLogData.notes}
        onChange={(e) => setCallLogData({
          ...callLogData,
          notes: e.target.value
        })}
        margin="normal"
        placeholder="Follow-up call completed. Patient is recovering well. Scheduled next appointment..."
      />
    </Box>
  );

  const renderContent = () => {
    switch (mode) {
      case 'update':
        return renderUpdateContent();
      case 'schedule':
        return renderScheduleContent();
      case 'log':
        return renderLogContent();
      default:
        return renderUpdateContent();
    }
  };

  const getSubmitButtonText = () => {
    if (loading) return <CircularProgress size={16} />;
    
    switch (mode) {
      case 'update':
        return 'Update Follow-up';
      case 'schedule':
        return 'Schedule Call';
      case 'log':
        return 'Log Call';
      default:
        return 'Submit';
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {getDialogTitle()}
        {lead && (
          <Box component="span" sx={{ fontSize: '0.875rem', fontWeight: 'normal', ml: 1 }}>
            - {lead.name}
          </Box>
        )}
      </DialogTitle>
      
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        {renderContent()}
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          disabled={loading}
        >
          {getSubmitButtonText()}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FollowUpDialog;